import React, { useEffect, useRef, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  Alert,
  Box,
  Button,
  Fab,
  Rating,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Editor } from '@tinymce/tinymce-react';
import { useCreateAISuggestionRecord, useGenerateContentWithOpenAI } from '@juno/client-api';
import {
  AISuggestionRecord,
  FeatureConfigTypeEnum,
  ForumTopic,
  OpenAIRequest,
  Tag,
} from '@juno/client-api/model';
import { FeatureConfig as FeatureConfigUtils } from '@juno/client-api/utils';
import { FEATURE_CONFIG_KEYS_ENUM } from '@juno/constants';
import { AutocompleteTagsGeneric, DrawerFooter, JunoSpin } from '@juno/ui';
import { MutationAction, onMutation, uploadTinyMceImageCloudinary, useSettings } from '@juno/utils';

interface TopicEditorSectionProps {
  sidebarTitle: string;
  handleSubmit: (newTopic: ForumTopic) => void;
  handleCancel: () => void;
  isSaving: boolean;
  editingTopic: ForumTopic | null;
  handleEditSubmit: (topic: ForumTopic) => void;
}

const TopicEditorSection: React.FC<TopicEditorSectionProps> = ({
  handleSubmit,
  handleCancel,
  isSaving,
  editingTopic,
  handleEditSubmit,
}) => {
  const { site, user, isSmallScreen, configs } = useSettings();
  const theme = useTheme(); // Use the theme
  const editorRef = useRef<any>(null);
  const [title, setTitle] = useState(editingTopic?.title || '');
  const [description, setDescription] = useState(editingTopic?.description || '');
  const [tagsState, setTagsState] = useState<Tag[]>(editingTopic?.tags || []);
  const [aiAssistMode, setAIAssistMode] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false); // State to control snackbar visibility
  const [fabPulse, setFabPulse] = useState(false); // State to control fab pulsing animation
  const [loading, setLoading] = useState(false); // State to control loading visibility
  const [wasAIGenerated, setWasAIGenerated] = useState(false); // State to control if AI was generated
  const [originalSuggestion, setOriginalSuggestion] = useState(''); // State to store the original suggestion
  const [rating, setRating] = useState<number | null>(null);
  const isAIAssistEnabled = FeatureConfigUtils.getFeatureFlag(
    configs,
    FEATURE_CONFIG_KEYS_ENUM.AIAssist,
  );

  const steps = ['Enter AI Assist', 'Generate', 'Review!'];

  useEffect(() => {
    if (isAIAssistEnabled) {
      const showSnackbarTimer = setTimeout(() => {
        setSnackbarOpen(true);
        setFabPulse(true); // Start pulsing the FAB
        const hideSnackbarTimer = setTimeout(() => {
          setSnackbarOpen(false);
          setFabPulse(false); // Stop pulsing the FAB
        }, 5000); // Hide after 5 seconds
        return () => clearTimeout(hideSnackbarTimer);
      }, 1000); // Show after 1 second
      return () => clearTimeout(showSnackbarTimer);
    }
  }, [isAIAssistEnabled]);

  const genSuggestion = useGenerateContentWithOpenAI(
    onMutation(MutationAction.CREATE, 'GenerateContentWithOpenAIParams', () => {}),
  );

  const recordAISuggestion = useCreateAISuggestionRecord(
    onMutation(MutationAction.CREATE, 'AISuggestionRecord', () => {}),
  );

  const handleAIAssist = () => {
    setAIAssistMode(true);
  };

  const handleGenerateSuggestions = async () => {
    setLoading(true);
    const basePrompt = `Generate json for an engaging discussion using the prompt. Ensure there is a key for the title and for description. This is going in a rich text editor so use bold, italics, and emojis to maximize engagement. Use the following prompt to generate the description: `;
    try {
      // This feels a bit hacky at this point BUT... there were a few times when testing
      // where this returned something not JSON serializable, and rerunning it solved it.
      // TODO: Before this gets to production we should add an error state.
      const suggestion = await genSuggestion.mutateAsync({
        siteId: site?.id || '',
        data: {
          prompt: basePrompt + prompt,
        } as OpenAIRequest,
      });

      const suggestionJson = JSON.parse(suggestion?.response || '');
      setTitle(suggestionJson.title);
      setDescription(suggestionJson.description);
      setOriginalSuggestion(suggestion?.response || '');
      setWasAIGenerated(true);
      setLoading(false);
      setAIAssistMode(false);
    } catch (e) {
      try {
        const suggestion = await genSuggestion.mutateAsync({
          siteId: site?.id || '',
          data: {
            prompt: basePrompt + prompt,
          } as OpenAIRequest,
        });

        const suggestionJson = JSON.parse(suggestion?.response || '');
        setTitle(suggestionJson.title);
        setDescription(suggestionJson.description);
        setOriginalSuggestion(suggestion?.response || '');
        setWasAIGenerated(true);
        setLoading(false);
        setAIAssistMode(false);
      } catch (e) {
        console.error(e);
        setLoading(false);
        // TODO: Add error page state
        setAIAssistMode(false);
      }
    }
  };

  const handleCreateTopic = () => {
    if (editingTopic) {
      handleEditSubmit({
        ...editingTopic,
        title,
        description,
        tags: tagsState,
      });
    } else {
      handleSubmit({
        title,
        description,
        tags: tagsState,
      } as ForumTopic);
      if (wasAIGenerated) {
        recordAISuggestion.mutateAsync({
          data: {
            suggested_text: originalSuggestion,
            final_text: JSON.stringify({ title, description }),
            prompt_used: prompt,
            rating: rating,
            relative_url: window.location.pathname + window.location.search,
            source: 'Discussion',
          } as AISuggestionRecord,
          siteId: site?.id || '',
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleGenerateSuggestions();
    }
  };

  return (
    <Box>
      {aiAssistMode ? (
        <Box>
          {loading ? (
            <Box sx={{ position: 'relative', height: '56px', width: '100%', ml: 19 }}>
              <JunoSpin />
            </Box>
          ) : (
            <TextField
              fullWidth
              size='small'
              rows={4}
              multiline
              placeholder='i.e. How can AI improve engagement in our association?'
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{ mb: 2 }}
            />
          )}
          <Stack
            direction={'row'}
            spacing={2}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Button
              variant='contained'
              fullWidth
              color='primary'
              disabled={loading}
              onClick={handleGenerateSuggestions}
            >
              Generate
            </Button>
            <Button
              variant='outlined'
              fullWidth
              color='secondary'
              disabled={loading}
              onClick={() => setAIAssistMode(false)}
            >
              Cancel
            </Button>
          </Stack>
          <Box sx={{ width: '100%', mt: 4 }}>
            <Stepper activeStep={1} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Box>
      ) : (
        <Stack spacing={2}>
          <TextField
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            name='Discussion Title'
            label='Discussion Title (required)'
            placeholder='Add Title...'
            variant='outlined'
            size='small'
            margin='dense'
            autoFocus
          />
          <Editor
            apiKey={process.env['NX_TINY_MCE_API_KEY']}
            onEditorChange={(value: string) => setDescription(value)}
            value={description || ''}
            onInit={(evt, editor) => (editorRef.current = editor)}
            init={{
              convert_urls: false,
              placeholder: 'Add Content...',
              highlight_on_focus: false,
              images_upload_handler: uploadTinyMceImageCloudinary,
              contextmenu: false,
              height: isSmallScreen ? 300 : 525,
              toolbar:
                'emoticons bold italic underline strikethrough link | alignleft aligncenter alignright alignjustify outdent indent | bullist numlist blockquote',
              menubar: false,
              statusbar: false,
              toolbar_location: 'bottom',
              setup: (editor) => {
                editor.setProgressState(true);
              },
              skin: 'material-outline',
            }}
            plugins={'autolink emoticons link'}
          />
          <AutocompleteTagsGeneric
            tags={tagsState || []}
            setTags={(tags) => setTagsState(tags)}
            siteId={site?.id || ''}
            label='Tags'
            popperPlacement='top'
          />
        </Stack>
      )}
      {!aiAssistMode && (
        <>
          <DrawerFooter
            onCancel={handleCancel}
            onSubmit={handleCreateTopic}
            disabled={!title || !description}
            submitting={isSaving}
          />

          {wasAIGenerated && (
            <Box sx={{ my: 2 }}>
              <Typography>How helpful was AI Assist?</Typography>
              <Rating
                name='ai-assist-rating'
                value={rating ? rating : 0}
                onChange={(event, newValue) => {
                  setRating(newValue);
                }}
              />
            </Box>
          )}
        </>
      )}
      {!aiAssistMode && isAIAssistEnabled && (
        <Tooltip title='AI Assist' placement='left'>
          <Fab
            color='primary'
            aria-label='AI Assist'
            onClick={handleAIAssist}
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              animation: fabPulse ? 'pulse 1.7s infinite' : 'none',
            }}
          >
            <AutoAwesomeIcon />
          </Fab>
        </Tooltip>
      )}
      <Box
        sx={{
          position: 'absolute',
          bottom: 80,
          left: 16,
          zIndex: 1300, // Ensure it's on top
        }}
      >
        <Snackbar
          open={snackbarOpen}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity='info'
            icon={<AutoAwesomeIcon />}
            sx={{
              width: '100%',
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.info.contrastText,
              '& .MuiAlert-icon': { color: theme.palette.info.contrastText },
            }}
          >
            Use the AI Assist button to generate suggestions for your discussion.
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default TopicEditorSection;
